import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { LuHourglass } from 'react-icons/lu';
import { FRANJAS } from '../../utils/franjas';
import { resumenVentanas } from '../../utils/ordenes';
import { getTodayISO } from '../../utils/dateUtils';

// Fecha tentativa (12-oct-2026): el admin asigna la visita pero el cliente todavía
// no confirmó el día. El técnico la ve en ámbar y no puede salir hasta que se confirme.

export const esTentativa = (o) => !!o?.sinConfirmar;

/** Etiqueta ámbar "Fecha tentativa" (chica, para listas y tarjetas). */
export function EtiquetaTentativa({ className = '' }) {
    return (
        <span className={`inline-flex items-center gap-1 text-label font-black px-2 py-0.5 rounded-md uppercase bg-[var(--warning-bg)] text-[var(--warning-tx)] ${className}`}>
            <LuHourglass size={11} /> Fecha tentativa
        </span>
    );
}

/** Elegir al asignar: ¿el cliente ya confirmó el día? */
export function SelectorConfirmacion({ tentativa, onChange }) {
    const op = (valor, texto) => (
        <button type="button" onClick={() => onChange(valor)}
            className={`flex-1 py-2 px-2 rounded-xl text-label font-black active:scale-95 transition-all ${
                tentativa === valor
                    ? (valor ? 'bg-[var(--warning-bg)] text-[var(--warning-tx)] ring-2 ring-[color:var(--warning-tx)]' : 'bg-brand-red text-white')
                    : 'bg-chip text-secondary'}`}>
            {texto}
        </button>
    );
    return (
        <div>
            <p className="text-label font-black text-muted uppercase tracking-widest mb-1.5">¿El cliente ya confirmó el día?</p>
            <div className="flex gap-2">
                {op(false, '✓ Sí')}
                {op(true, '⏳ Todavía no')}
            </div>
            {tentativa && (
                <p className="mt-1.5 text-caption text-muted">
                    Queda tentativa: el técnico la ve pero no puede salir hasta que la confirmes.
                </p>
            )}
        </div>
    );
}

/**
 * Técnico, visita con fecha tentativa (20-oct-2026): responde si puede ese día y horario antes de que
 * confirme el cliente. "Puedo" le avisa al admin y, cuando el cliente confirma, ya no hace falta un
 * segundo "Ok, voy". "No puedo" le avisa al admin para pasarla a otro técnico o a otro día.
 */
export function RespuestaTentativa({ orden, onCambio }) {
    const [noPuedo, setNoPuedo] = useState(false);
    const [motivo, setMotivo] = useState('');
    const [propuesta, setPropuesta] = useState('');   // 1-nov-2026: cuándo sí puede (opcional)
    const [enviando, setEnviando] = useState(false);
    const enviar = async (fn, ok) => {
        setEnviando(true);
        try { await fn(); toast.success(ok); setNoPuedo(false); onCambio && onCambio(); }
        catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo avisar'); }
        finally { setEnviando(false); }
    };
    const puedo = () => enviar(() => api.patch(`/ordenes/${orden.id}/confirmar`), 'Listo, el admin ya sabe que podés');
    const avisarNoPuedo = () => enviar(() => api.post(`/ordenes/${orden.id}/no-puede`, {
        motivo: [propuesta ? `Puede ${propuesta}` : null, motivo.trim() || null].filter(Boolean).join(' · ').slice(0, 200) || null,
    }), propuesta ? 'Listo, el admin se lo propone al cliente' : 'Listo, el admin ya sabe que no podés');
    const caja = 'mt-2.5 p-3 rounded-xl border border-[color:var(--warning-tx)]/40 bg-[var(--warning-bg)] space-y-2';
    const stop = (e) => e.stopPropagation();

    if (orden.confirmadaEn) {
        return (
            <div className={caja} onClick={stop}>
                <p className="text-body font-black text-[var(--warning-tx)]">✓ Le dijiste que podés</p>
                <p className="text-caption text-ink">Falta que confirme el cliente: no vayas hasta que te llegue el aviso. Cuando confirme, queda lista (no hace falta otro «Ok, voy»).</p>
                <button type="button" disabled={enviando} onClick={() => setNoPuedo(true)} className="text-caption font-bold text-muted underline">Al final no puedo</button>
                {noPuedo && <FormNoPuedo orden={orden} propuesta={propuesta} setPropuesta={setPropuesta} motivo={motivo} setMotivo={setMotivo} enviando={enviando} onEnviar={avisarNoPuedo} onCancelar={() => setNoPuedo(false)} />}
            </div>
        );
    }
    if (orden.noPuedeEn) {
        return (
            <div className={caja} onClick={stop}>
                <p className="text-body font-black text-[var(--warning-tx)]">Avisaste que no podés</p>
                <p className="text-caption text-ink">El admin la va a pasar a otro técnico o a otro día.{orden.noPuedeMotivo ? ` (${orden.noPuedeMotivo})` : ''}</p>
                <button type="button" disabled={enviando} onClick={puedo} className="text-caption font-bold text-muted underline">En realidad sí puedo</button>
            </div>
        );
    }
    return (
        <div className={caja} onClick={stop}>
            {/* 1-nov-2026: que se entienda que es un trabajo que se está coordinando con el cliente para ese día y hora */}
            <p className="text-body font-black text-[var(--warning-tx)]">Lo estamos coordinando con el cliente</p>
            <p className="text-caption text-ink">Le estamos proponiendo este día y horario. ¿Podés? Si no, avisá y le ofrecemos otro.</p>
            {noPuedo ? (
                <FormNoPuedo orden={orden} propuesta={propuesta} setPropuesta={setPropuesta} motivo={motivo} setMotivo={setMotivo} enviando={enviando} onEnviar={avisarNoPuedo} onCancelar={() => setNoPuedo(false)} />
            ) : (
                <div className="flex gap-2">
                    <button type="button" disabled={enviando} onClick={puedo}
                        className="flex-1 h-11 rounded-xl bg-[color:var(--etapa-listo)] text-white text-body font-black active:scale-95 disabled:opacity-50">Puedo ese día</button>
                    <button type="button" disabled={enviando} onClick={() => setNoPuedo(true)}
                        className="flex-1 h-11 rounded-xl bg-card text-ink text-body font-black border border-black/10 dark:border-white/10 active:scale-95 disabled:opacity-50">No puedo</button>
                </div>
            )}
        </div>
    );
}

// Próximos días de trabajo (sin domingo), para proponer otro
function proximosDias(n = 6) {
    const out = [];
    const d = new Date(`${getTodayISO()}T12:00:00`);
    while (out.length < n) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() === 0) continue;
        const t = d.toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric' }).replace('.', '');
        out.push(t.charAt(0).toUpperCase() + t.slice(1));
    }
    return out;
}
const chip = (on) => `h-9 px-3 rounded-full text-label font-bold border-2 active:scale-95 ${on ? 'border-brand-red bg-[rgba(232,66,47,0.10)] text-ink' : 'border-transparent bg-card text-secondary'}`;

// 1-nov-2026: al decir que no puede, el técnico puede decir cuándo sí. Si el cliente marcó días y horarios,
// se elige entre esos; si no, un día y una franja. El admin lo ve y se lo propone al cliente.
function ProponerOtro({ orden, propuesta, setPropuesta }) {
    const delCliente = resumenVentanas(orden?.ventanasCliente);
    const [dia, setDia] = useState('');
    const [franja, setFranja] = useState('');
    const elegir = (d, f) => { setDia(d); setFranja(f); setPropuesta(d && f ? `${d} de ${f}` : ''); };
    if (delCliente.length) {
        return (
            <div className="space-y-1.5">
                <p className="text-caption font-bold text-ink">¿Cuándo podrías? El cliente puede:</p>
                <div className="flex flex-wrap gap-1.5">
                    {delCliente.map(v => <button key={v} type="button" onClick={() => setPropuesta(propuesta === v ? '' : v)} className={chip(propuesta === v)}>{v}</button>)}
                </div>
            </div>
        );
    }
    return (
        <div className="space-y-1.5">
            <p className="text-caption font-bold text-ink">¿Cuándo podrías? <span className="font-normal text-muted">(opcional)</span></p>
            <div className="flex flex-wrap gap-1.5">
                {proximosDias().map(d => <button key={d} type="button" onClick={() => elegir(dia === d ? '' : d, franja)} className={chip(dia === d)}>{d}</button>)}
            </div>
            <div className="flex flex-wrap gap-1.5">
                {FRANJAS.map(f => <button key={f.id} type="button" onClick={() => elegir(dia, franja === f.corto ? '' : f.corto)} className={chip(franja === f.corto)}>{f.corto}</button>)}
            </div>
        </div>
    );
}

function FormNoPuedo({ orden, propuesta, setPropuesta, motivo, setMotivo, enviando, onEnviar, onCancelar }) {
    return (
        <div className="space-y-2">
            <ProponerOtro orden={orden} propuesta={propuesta} setPropuesta={setPropuesta} />
            <input value={motivo} onChange={e => setMotivo(e.target.value)} maxLength={200} placeholder="¿Por qué? (opcional)"
                aria-label="Motivo" className="w-full h-10 px-3 rounded-xl bg-card text-ink text-body outline-none placeholder:text-muted" />
            <div className="flex gap-2">
                <button type="button" onClick={onCancelar} className="flex-1 h-10 rounded-xl bg-chip text-secondary text-label font-black">Volver</button>
                <button type="button" disabled={enviando} onClick={onEnviar} className="flex-[2] h-10 rounded-xl bg-[#C9341F] text-white text-label font-black disabled:opacity-50">{propuesta ? `Proponer ${propuesta}` : 'Avisar que no puedo'}</button>
            </div>
        </div>
    );
}
